import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { useUserId } from '@/auth/UserIdContext.tsx';
import { useChatService } from '@/bridge/ChatService.ts';
import { ResizeHandle } from '@/components/ResizeHandle.tsx';
import { useExitAnimation } from '@/hooks/useExitAnimation.ts';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useOpenChatPanel } from '@/hooks/useOpenChatPanel.ts';
import { useResizePanel } from '@/hooks/useResizePanel.ts';
import { useTypingPlaceholder } from '@/hooks/useTypingPlaceholder.ts';
import { ActionBar, type ActionBarHandle } from '@/spaces/chat/ActionBar.tsx';
import { AgentActivityFeed } from '@/spaces/chat/AgentActivityFeed.tsx';
import {
	EmptyStateGreeting,
	EmptyStatePills,
} from '@/spaces/chat/ChatEmptyState.tsx';
import { EMPTY_STATE_PROMPTS } from '@/spaces/chat/chatEmptyStateData';
import { ChatHeader } from '@/spaces/chat/ChatHeader.tsx';
import { ChatHistoryToggle } from '@/spaces/chat/ChatHistoryToggle.tsx';
import { ChatInputSeedProvider } from '@/spaces/chat/ChatInputSeedContext.tsx';
import { ChatLayout } from '@/spaces/chat/ChatLayout';
import { ChatMessageList } from '@/spaces/chat/ChatMessageList.tsx';
import { JobsWidget } from '@/spaces/chat/JobsWidget.tsx';
import { ChatHistoryPanel } from '@/spaces/chat/panels/ChatHistoryPanel';
import { ChatPanel } from '@/spaces/chat/panels/ChatPanel';
import { StrategyDock } from '@/spaces/chat/strategy-dock/StrategyDock';
import { useChatThreads } from '@/spaces/chat/useChatThreads.ts';
import { cn } from '@/utils/cn.ts';

const HISTORY_PANEL_WIDTH = 280;

// A dialog, an alert dialog and a menu all close on Escape, and each one runs its
// own window or document listener. Those listeners cannot be ordered against this
// one, because listener order follows registration order and an overlay mounts
// later. So the stop asks the document instead: while one of these is open, the
// key press belongs to it.
//
// An overlay owns the key only while it is really open, and only while it really
// listens. Two states look open in the document but own nothing:
//
// A closing React Aria overlay stays mounted for its exit animation. It marks the
// popover or modal wrapper with data-exiting, while the role sits on a descendant
// element, so the ancestor is what must be tested, not the element that carries
// the role.
//
// The image editor keeps its dialog mounted while it uploads, and it ignores
// Escape in that window. It marks itself data-escape-inert, so the stop stays
// reachable there.
const OVERLAY_ROLE_SELECTOR =
	'[role="dialog"],[role="alertdialog"],[role="menu"]';
const INERT_OVERLAY_SELECTOR = '[data-exiting],[data-escape-inert]';

const hasOpenOverlay = (): boolean =>
	Array.from(document.querySelectorAll(OVERLAY_ROLE_SELECTOR)).some(
		(el) => el.closest(INERT_OVERLAY_SELECTOR) === null,
	);

interface CollapsibleRowProps {
	visible: boolean;
	children: React.ReactNode;
	className?: string;
	style?: React.CSSProperties;
}

const CollapsibleRow = ({
	visible,
	children,
	className,
	style,
}: CollapsibleRowProps) => (
	<div
		className={cn(
			`grid overflow-hidden transition-[grid-template-rows,opacity,flex-grow]
			duration-300 ease-in-out`,
			className,
		)}
		style={{
			gridTemplateRows: visible ? '1fr' : '0fr',
			opacity: visible ? 1 : 0,
			...style,
		}}
		aria-hidden={!visible}
	>
		<div className={cn('min-h-0', !visible && 'pointer-events-none')}>
			{children}
		</div>
	</div>
);

export const Chat = () => {
	const bridgeApi = useBridgeApi();
	const openChatPanel = useOpenChatPanel();
	const [chatService] = useChatService();
	const userId = useUserId();
	const [isHistoryPanelOpen, setIsHistoryPanelOpen] = useState(false);
	const actionBarRef = useRef<ActionBarHandle>(null);
	const prevChatIdRef = useRef<string | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const chatLayoutRef = useRef<HTMLDivElement>(null);
	// The dock hangs from the header band and the chat column reserves space for it, so
	// the same gate drives both. It also drives the strategy panel: chat_panel is saved
	// state and outlives the release, so an owner who loses the flag would otherwise keep
	// a panel the dock can no longer explain. Without it the column renders as before.
	const strategiesEnabled = useFeatureFlag('strategies');
	const { threads, loaded: threadsLoaded } = useChatThreads();
	const userVisibleThreads = threads.filter(
		(thread) => thread.is_setup_only !== true,
	);
	const hasUnreadThreads = useMemo(
		() => threads.some((t) => t.is_unread),
		[threads],
	);

	useEffect(() => {
		chatService.setUserId(userId);
	}, [userId, chatService]);

	const currentChatId = bridgeApi.user_state?.current_chat_id ?? null;
	const onboardingDone =
		bridgeApi.user_state?.onboarding_status?.phase === 'done';
	const [isChatInitInFlight, setIsChatInitInFlight] = useState(false);

	useEffect(() => {
		if (
			!userId ||
			currentChatId !== null ||
			bridgeApi.connectionStatus !== 'connected'
		) {
			if (isChatInitInFlight) setIsChatInitInFlight(false);
			return;
		}
		if (!threadsLoaded || isChatInitInFlight) return;
		setIsChatInitInFlight(true);
		// After onboarding, or when there are no user-visible threads, land the
		// user in a fresh empty chat. Never auto-switch into a background
		// setup-only thread (those are filtered out of userVisibleThreads).
		if (onboardingDone || userVisibleThreads.length === 0) {
			bridgeApi.sendAction({ type: 'do_create_chat' });
		} else {
			bridgeApi.sendAction({
				type: 'do_switch_chat',
				chat_id: userVisibleThreads[0].id,
			});
		}
	}, [
		currentChatId,
		userId,
		onboardingDone,
		userVisibleThreads,
		threadsLoaded,
		bridgeApi.connectionStatus,
		isChatInitInFlight,
		bridgeApi,
	]);

	useEffect(() => {
		return () => {
			bridgeApi.sendAction({ type: 'do_sync_current_chat' });
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	const currentChat = currentChatId
		? bridgeApi.user_state?.chats?.[currentChatId]
		: null;
	const messages = currentChat ? (currentChat.messages ?? []) : [];
	const savedPanel = bridgeApi.user_state?.chat_panel ?? null;
	// Gate the panel where it is DECIDED, not only where it draws: isExternalPanelOpen
	// below reserves the split from this value alone, so a null-returning ChatPanel would
	// still leave an empty half-column with a live resize handle and no close control.
	const chatPanel =
		savedPanel?.panel_type === 'strategy' && !strategiesEnabled
			? null
			: savedPanel;

	const {
		shouldRender: shouldRenderHistoryPanel,
		isClosing: isHistoryPanelClosing,
	} = useExitAnimation(isHistoryPanelOpen);

	const currentPhase = currentChat?.current_phase ?? null;
	const isActionBarDisabled = currentChat?.is_read_only === true;
	const isChatLoading = currentChat?.is_loading === true;
	const isWaitingForChat =
		userId !== null && (currentChatId === null || currentChat == null);
	const isChatSendDisabled =
		isActionBarDisabled ||
		bridgeApi.connectionStatus !== 'connected' ||
		isWaitingForChat;
	const hasNoMessages = messages.length === 0;
	// A thread opened from a notification, dock card or job is never a blank new chat. The
	// bridge forces an agent turn for every thread that carries a linked_item, so its first
	// message is already on its way, and no state field is true during that gap. Greeting the
	// owner there tells them the thread is empty when it is not. The bare "new chat" paths
	// pass no linked_item, so they still greet.
	//
	// `isEmpty` drives the empty-chat LAYOUT, and the layout consults nothing else: it raises
	// the aurora wash, grows the composer to the middle of the column, and swaps in the
	// animated suggestion placeholder. So a linked thread must be excluded here and not only
	// from `showEmptyStateContent` below, or the owner reads a blank new chat for as long as
	// the first message takes to arrive.
	const isEmpty = hasNoMessages && currentChat?.linked_item == null;
	const hasLinkedItem = hasNoMessages && currentChat?.linked_item != null;
	const [awaitTimedOut, setAwaitTimedOut] = useState(false);
	useEffect(() => {
		// Reset BEFORE arming, not only when the thread stops being linked. This
		// effect re-runs on a linked-to-linked switch, and resetting only in the
		// early return left the previous thread's `true` in place — so the next
		// linked thread showed the timed-out line at t=0 and never got its wait.
		// <Chat /> carries no key (Spaces.tsx), so this state survives the switch.
		setAwaitTimedOut(false);
		if (!hasLinkedItem) return;
		const timer = setTimeout(() => setAwaitTimedOut(true), 60_000);
		return () => clearTimeout(timer);
	}, [hasLinkedItem, currentChatId]);
	const isAwaitingFirstReply = hasLinkedItem && !awaitTimedOut;
	const showEmptyStateContent =
		isEmpty && !isChatLoading && !isAwaitingFirstReply;
	const animatedPlaceholder = useTypingPlaceholder(
		EMPTY_STATE_PROMPTS,
		isEmpty,
	);

	const lastMessage = messages[messages.length - 1];
	// A turn is in progress for its whole length, not only while text arrives.
	// The phase covers the long silent stretches — thinking, tool calls, and
	// finalizing — which is exactly when a user wants to stop. Every non-null
	// phase counts, including responding and finalizing: a run costs the most
	// to let finish at the end. The streaming flag stays as a second signal,
	// because it covers the window before the first phase event lands. This
	// rule is deliberately wider than the thinking spinner's in
	// ChatMessageList; a spinner hides once tokens show, a stop button must not.
	const isGenerating =
		currentPhase != null ||
		(lastMessage?.type === 'agent_message' &&
			lastMessage.is_streaming === true);

	useEffect(() => {
		if (
			prevChatIdRef.current !== null &&
			currentChatId !== prevChatIdRef.current
		) {
			actionBarRef.current?.focus();
		}
		prevChatIdRef.current = currentChatId;
	}, [currentChatId]);

	const handleNewChat = useCallback(() => {
		bridgeApi.sendAction({ type: 'do_create_chat' });
	}, [bridgeApi]);

	const handleToggleHistory = useCallback(() => {
		setIsHistoryPanelOpen((prev) => !prev);
	}, []);

	const handleStopGenerating = useCallback(() => {
		bridgeApi.sendAction({ type: 'do_stop_generation' });
	}, [bridgeApi]);

	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			// Escape carries no modifier, so it is handled before the modifier
			// guard below. Only claim the key while a run is in progress; the rest
			// of the time Escape keeps its usual job of dismissing whatever is
			// open, and the event is left to travel to those handlers untouched.
			if (e.code === 'Escape') {
				// A CJK input method owns this key press: it dismisses the candidate
				// list. The candidate window is drawn by the operating system, so
				// hasOpenOverlay below cannot see it. Without this the customer has no
				// way to cancel a candidate that does not also kill the run.
				if (e.isComposing) return;
				if (!isGenerating) return;
				// An open overlay owns this key press, because it closes on the same
				// press. Stopping the run as well would end work the user never asked
				// to end. The user asked to dismiss the overlay.
				if (hasOpenOverlay()) return;
				e.preventDefault();
				handleStopGenerating();
				return;
			}
			if (!(e.metaKey || e.ctrlKey)) return;
			if (e.altKey) return;
			if (e.code === 'KeyJ') {
				e.preventDefault();
				handleNewChat();
			} else if (e.code === 'Slash') {
				e.preventDefault();
				handleToggleHistory();
			}
		};
		window.addEventListener('keydown', handler);
		return () => window.removeEventListener('keydown', handler);
	}, [handleNewChat, handleToggleHistory, handleStopGenerating, isGenerating]);

	const handleSelectChat = useCallback(
		(chatId: string) => {
			bridgeApi.sendAction({ type: 'do_switch_chat', chat_id: chatId });
		},
		[bridgeApi],
	);

	const handleArtifactClick = useCallback(
		(
			panelType: string,
			itemId: string,
			artifactType?: string,
			strategyId?: string,
		) => {
			openChatPanel({
				panel_type: panelType,
				item_id: itemId,
				...(artifactType && { artifact_type: artifactType }),
				// A request or proposal card is keyed by the request or the proposal, so the bridge
				// needs this to resolve the strategy the card names.
				...(strategyId && { strategy_id: strategyId }),
			});
		},
		[openChatPanel],
	);

	const isExternalPanelOpen = chatPanel !== null;

	const { leftFraction, handleMouseDown, isDragging } = useResizePanel({
		containerRef,
		availableWidthOffset: shouldRenderHistoryPanel ? HISTORY_PANEL_WIDTH : 0,
	});

	const chatLayoutStyle = isExternalPanelOpen
		? { flex: `${leftFraction} 1 0%` }
		: undefined;
	const rightPanelStyle = isExternalPanelOpen
		? { flex: `${1 - leftFraction} 1 0%` }
		: undefined;

	return (
		<ChatInputSeedProvider>
			<div
				ref={containerRef}
				className="relative flex h-full w-full overflow-hidden"
			>
				{shouldRenderHistoryPanel && (
					<ChatHistoryPanel
						threads={threads}
						currentChatId={currentChatId}
						onSelectChat={handleSelectChat}
						isClosing={isHistoryPanelClosing}
					/>
				)}
				<ChatLayout
					rootRef={chatLayoutRef}
					className={cn(
						isExternalPanelOpen ? '' : 'flex-1',
						shouldRenderHistoryPanel && 'rounded-l-none',
					)}
					style={chatLayoutStyle}
					isPanelOpen={false}
					floatingHeader={strategiesEnabled}
					headerContent={<ChatHeader onNewChat={handleNewChat} />}
				>
					<div
						className={cn(
							`relative isolate flex h-full flex-col px-3 pb-3
							transition-[padding-top] duration-300 ease-in-out`,
							strategiesEnabled && isEmpty ? 'pt-[61px]' : 'pt-3',
						)}
					>
						<div
							aria-hidden="true"
							className={cn(
								`pointer-events-none absolute inset-x-3 bottom-3 z-[-1]
								overflow-hidden rounded-2xl`,
								strategiesEnabled ? 'top-[61px]' : 'top-3',
							)}
						>
							<div
								data-testid="chat-empty-aurora"
								className="absolute inset-0 overflow-hidden
									transition-[opacity,transform] duration-700 ease-in-out"
								style={{
									background: '#F5F0EC',
									WebkitMaskImage:
										'linear-gradient(to bottom, black 35%, transparent 60%)',
									maskImage:
										'linear-gradient(to bottom, black 35%, transparent 60%)',
									opacity: isEmpty ? 1 : 0,
									transform: isEmpty ? 'translateY(0)' : 'translateY(-48px)',
								}}
							>
								<div
									className="onb-aurora-blob"
									style={
										{
											'--blob-color': '#6CCEF5',
											'--blob-x1': '8vw',
											'--blob-y1': '-12vh',
											'--blob-x2': '38vw',
											'--blob-y2': '-42vh',
											'--blob-s1': '1.25',
											'--blob-s2': '0.9',
											'--blob-duration': '11s',
										} as React.CSSProperties
									}
								/>
								<div
									className="onb-aurora-blob"
									style={
										{
											'--blob-color': '#F5C48A',
											'--blob-x1': '-16vw',
											'--blob-y1': '12vh',
											'--blob-x2': '-46vw',
											'--blob-y2': '-22vh',
											'--blob-s1': '1.15',
											'--blob-s2': '0.88',
											'--blob-duration': '13s',
											'--blob-delay': '0s',
										} as React.CSSProperties
									}
								/>
								<div
									className="onb-aurora-blob"
									style={
										{
											'--blob-color': '#C8BEF0',
											'--blob-x1': '2vw',
											'--blob-y1': '-20vh',
											'--blob-x2': '-22vw',
											'--blob-y2': '10vh',
											'--blob-s1': '1.3',
											'--blob-s2': '0.95',
											'--blob-duration': '15s',
											'--blob-delay': '0s',
										} as React.CSSProperties
									}
								/>
								<div
									className="onb-aurora-blob"
									style={
										{
											'--blob-color': '#F5D8A0',
											'--blob-x1': '-10vw',
											'--blob-y1': '8vh',
											'--blob-x2': '-40vw',
											'--blob-y2': '38vh',
											'--blob-s1': '1.2',
											'--blob-s2': '0.85',
											'--blob-duration': '14s',
											'--blob-delay': '0s',
										} as React.CSSProperties
									}
								/>
								<div
									className="onb-aurora-blob"
									style={
										{
											'--blob-color': '#C4A4D4',
											'--blob-x1': '12vw',
											'--blob-y1': '14vh',
											'--blob-x2': '42vw',
											'--blob-y2': '42vh',
											'--blob-s1': '1.2',
											'--blob-s2': '0.9',
											'--blob-duration': '17s',
											'--blob-delay': '0s',
										} as React.CSSProperties
									}
								/>
							</div>
						</div>
						<div
							className="flex min-h-0 overflow-hidden transition-[flex-grow]
								duration-300 ease-in-out"
							style={{ flexGrow: isEmpty ? 0 : 1, flexShrink: 1, flexBasis: 0 }}
						>
							{isChatLoading || isAwaitingFirstReply ? (
								<div className="flex flex-1 items-center justify-center">
									<span className="text-sm text-(--text-muted)">
										Loading...
									</span>
								</div>
							) : hasLinkedItem ? (
								// The 60-second timer has released the wait, and the thread still holds
								// no messages. `isEmpty` is false here, so the branch below would paint
								// an empty message list and the owner would read a column with nothing
								// in it. The greeting is the wrong answer for a thread that is about
								// something, so say what happened instead. The composer stays open.
								<div
									data-testid="chat-await-timed-out"
									className="flex flex-1 items-center justify-center px-6"
								>
									<span className="text-center text-sm text-(--text-muted)">
										This conversation did not start. Write a message to begin.
									</span>
								</div>
							) : !isEmpty ? (
								<ChatMessageList
									messages={messages}
									chatId={currentChatId ?? ''}
									currentPhase={currentPhase}
									onArtifactClick={handleArtifactClick}
									topFade={strategiesEnabled}
								/>
							) : null}
						</div>

						<div
							className="mx-auto flex w-full max-w-4xl flex-col px-6
								transition-[flex-grow,padding-bottom] duration-300 ease-in-out"
							style={{
								flexGrow: isEmpty ? 1 : 0,
								flexShrink: 0,
								flexBasis: 'auto',
								paddingBottom: isEmpty ? '0px' : '1rem',
							}}
						>
							<div
								aria-hidden="true"
								className="transition-[flex-grow] duration-300 ease-in-out"
								style={{
									flexGrow: isEmpty ? 1 : 0,
									flexShrink: 0,
									flexBasis: 0,
								}}
							/>
							<CollapsibleRow visible={showEmptyStateContent}>
								<div className="pb-6">
									<EmptyStateGreeting />
								</div>
							</CollapsibleRow>

							<div
								className={cn(
									`rounded-2xl bg-white/50 backdrop-blur-md transition-all
									duration-300`,
									isEmpty ? 'px-3 pt-3 pb-3' : 'p-1',
								)}
							>
								<ActionBar
									ref={actionBarRef}
									chatService={chatService}
									isDisabled={isChatSendDisabled}
									isGenerating={isGenerating}
									enableAttachments
									showDisclaimer={!isEmpty}
									overlayPlaceholder={
										isEmpty ? animatedPlaceholder : 'Write a message...'
									}
								/>
								<CollapsibleRow visible={showEmptyStateContent}>
									<div className="px-3 pt-2 pb-2.5">
										<EmptyStatePills
											threadsLoaded={threadsLoaded}
											hasZeroThreads={userVisibleThreads.length === 0}
											isChatLoading={isChatLoading || isChatSendDisabled}
										/>
									</div>
								</CollapsibleRow>
							</div>

							<CollapsibleRow
								visible={showEmptyStateContent}
								className="min-h-0 transition-[flex-grow,flex-basis]
									duration-300 ease-in-out"
								style={{
									flexGrow: isEmpty ? 1 : 0,
									flexShrink: 1,
									flexBasis: isEmpty ? '16rem' : '0px',
								}}
							>
								<div
									className="grid h-full auto-rows-fr grid-cols-1 gap-4 pt-8
										sm:grid-cols-2"
								>
									<div
										className="flex min-h-40 flex-col rounded-2xl bg-white/50
											p-4 backdrop-blur-md sm:min-h-56"
									>
										<JobsWidget />
									</div>
									<div
										className="flex min-h-40 flex-col rounded-2xl bg-white/50
											p-4 backdrop-blur-md sm:min-h-56"
									>
										<AgentActivityFeed />
									</div>
								</div>
							</CollapsibleRow>
						</div>
					</div>
				</ChatLayout>

				{isExternalPanelOpen && chatPanel && (
					<>
						<ResizeHandle
							onMouseDown={handleMouseDown}
							isDragging={isDragging}
						/>
						<ChatPanel panel={chatPanel} style={rightPanelStyle} />
					</>
				)}

				<div className="pointer-events-none absolute top-3 left-4 z-10">
					<div className="pointer-events-auto">
						<ChatHistoryToggle
							onToggle={handleToggleHistory}
							isHistoryOpen={isHistoryPanelOpen}
							hasUnreadThreads={hasUnreadThreads}
						/>
					</div>
				</div>

				<StrategyDock columnRef={chatLayoutRef} />
			</div>
		</ChatInputSeedProvider>
	);
};
